/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { InputConditions } from '../rules/index';
import { PlacementType, Visibility } from '../site/placement/constants';
import type { CampaignFrequency, PageSignals } from '../activity/frequency';
import type { AuctionLockedFacts } from '../auction';
import type { AdvertorialConfig } from './advertorial';
import type { Dcfg } from './collection';
import type { UserConsent } from './prebid';

export type KeyValue = {
  key: string;
  value: string;
};

export type AdAssignedKeyValues = {
  id: string;
  kvs: KeyValue[];
  l?: string;
};

export type PlacementAssignedKeyValues = {
  gad: string;
  kvs?: KeyValue[];
  ads?: AdAssignedKeyValues[];
};

/**
 * Eligible ad object carried in the select payload's `eads` array. Ids mirror
 * the legacy `edids` entries (which `eads` deprecates).
 */
export type EligibleAd = {
  id: string;
};

/**
 * Per-campaign entry in the select payload. Carries the campaign-level
 * exposure snapshot (cel/ceh/ced/cew); the server resolves brand/advertiser
 * identity from the campaign id itself.
 */
export type CampaignEligibleAds = CampaignFrequency & {
  id: string;
  /** Legacy eligible ad delivery ids. Retired once the backend reads `eads`. */
  edids: string[];
  /** Eligible ad objects (ids mirror `edids`, which they deprecate). */
  eads: EligibleAd[];
};

export type AdKeyValues = {
  id: string;
  edids: string[];
};

export type SelectionEndpointPlacementData = {
  gad: string;
  ss: { h: number; w: number }[];
  v: number;
  t: PlacementType;
  campaigns: CampaignEligibleAds[];
};

export type SelectionEndpointRequestPayload = {
  plcmts: SelectionEndpointPlacementData[];
};

export type SelectionEndpointResponsePayload = {
  l?: string;
  plcmts: PlacementAssignedKeyValues[];
};

export type SelectionReportingPlacementData = {
  gad: string;
  ads: AdAssignedKeyValues[];
};

export type SelectionReportingEndpointRequestPayload = {
  plcmts: SelectionReportingPlacementData[];
};

export type AdRules = {
  conditions: InputConditions;
};

export type AdData = {
  id: string;
  geid?: string;
  elid?: string;
  eid?: string;
  eurl?: string;
  // Server-set: report this ad's rule-evaluation failures via debug events
  dbg?: boolean;
  rules: AdRules;
};

export enum CampaignType {
  DIRECT = '0',
  DEAL = '1',
  LEARNING_DIRECT = '2',
  LEARNING_PROGRAMMATIC = '3',
  SCORING_DIRECT = '4',
  SCORING_DEAL = '5',
  MONITORING = '6',
}

export enum AdIdType {
  EXTERNAL_ID = 'external_id',
  DELIVERY_ID = 'delivery_id',
}

export type CampaignData = {
  ads: AdData[];
  deals?: string[];
  id: string;
  geids?: string[];
  bkwds?: string;
  type: CampaignType;
  rules?: AdRules;
};

export type StoredData = {
  facts: Record<string, string>;
  property: PropertyConfig;
  campaigns: CampaignData[];
};

export enum SiteEnabledFeature {
  VSCORES = 'vscores',
}

export type AdUnitTransformerConfig = {
  name: 'dropLastSegment';
  delimiter?: string;
};

export type PropertyConfig = {
  id: string;
  advid: string | null;
  acids: string[];
  ctslctrs: string;
  dlail: string[];
  vscores: boolean;
  maxRecordedAuctions?: number;
  advtl?: AdvertorialConfig;
  eidsrc?: string;
  adUnitTransformer?: AdUnitTransformerConfig;
  rules?: AdRules;
  dcfg?: Dcfg;
};

export type RenderedAdData = {
  advertiserDomains?: string[];
  advertiserId?: string;
  lineItemId?: string;
  creativeId?: string;
  nodalsDealId?: string;
  responseIdentifier: string;
  isProgrammaticBuy: boolean;
  isPrebidAd: boolean;
  isNodalsTag?: boolean;
  size?: PlacementSize;
  durationSeconds?: number;
};

export type BaseEventAnalyticsData = {
  opportunityId: string;
  deliveryId?: string;
  adUnit: string;
  pvid: string;
  sid: string;
  uid?: string;
  advertiserId?: string;
  advertiserDomains?: string[];
  lineItemId?: string;
  nodalsDealId?: string;
  selectionLabel?: string;
};

export type GenericEventAnalyticsData = BaseEventAnalyticsData & {
  impressionId: string;
  userConsent: UserConsent;
  useHttpPost?: boolean;
  t: number;
};

export type ImpressionAnalyticsData = BaseEventAnalyticsData & {
  id: string;
  kvs?: KeyValue[];
  creativeId?: string;
  renderedData?: { size: PlacementSize };
  durationSeconds?: number;
  t: number;
  v: Visibility;
  pt: PlacementType;
  /**
   * Auction-time snapshot for this impression: the opportunity's page signals
   * (present for every tracked auction — learning/monitoring/scoring included)
   * merged with the campaign-exposure values (present only when the delivery
   * went through a selection). Identical to the values the model saw ("what
   * the model sees is what we record"). Absent only when the impression had no
   * tracked auction at all; absent means unknown, never zero.
   */
  xd?: Partial<CampaignFrequency> & Partial<PageSignals>;
  /**
   * The pcids/bw/bh values exactly as this impression's select payload carried
   * them. Absent when the render had no auction — the recorder then reads the
   * live values instead.
   */
  lockedFacts?: AuctionLockedFacts;
};

export type PlacementSize = { width: number; height: number } | null;

export { PlacementType, Visibility };
